import React from "react";
import {observer} from "mobx-react-lite";
import {Button, IconButton, InputAdornment, TextField} from "@mui/material";
import {Add, Clear, Search} from "@mui/icons-material";
import {generatePath, useNavigate} from "react-router";
import {sitRoutes} from "~/sit/routing/sitRoutes.ts";
import {useSitStores} from "~/stores";
import type {DOI} from "~/sit/ApiTypes.ts";
import {category_readable_str, ComplexityTierValues, DOIs} from "~/sit/ApiTypes.ts";
import {complexityTierLabel, sitColumnLabel, sitFieldLabel, stageGateLabel} from "~/sit/sitLabels";
import type {InitiativeFilterValues} from "~/sit/stores/InitiativeListStore.ts";
import {SitFilterBar, type SitFilterDef, SitOptionList} from "../../components/filters/SitFilterBar";
import {sitFilterStatus, useSitFilterDraft, withExcluded} from "../../components/filters/sitFilterDraft";
import i18n from "../../../i18n/i18n";

const NO_FILTERS: InitiativeFilterValues = {
    my_initiatives: 0, include_deactivated: false, categories: [], companies: [], dois: [], complexity_tiers: [],
    yearsByDoi: {1: [], 2: [], 3: [], 4: [], 5: []}, excluded: [],
};

export const InitiativeFilters: React.FC = observer(() => {
    const {initiativeListStore: store, ccStore} = useSitStores();
    const navigate = useNavigate();
    const [exporting, setExporting] = React.useState(false);
    const yearOptions = Array.from({length: 6}, (_v, i) => new Date().getFullYear() - 2 + i);

    // Download the (currently filtered) initiative list as an Efficiency-Map .xlsx.
    const onExport = () => {
        setExporting(true);
        store.exportInitiatives().then((resp) => {
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'mapa_de_eficiencias.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        }).finally(() => setExporting(false));
    };

    const applied: InitiativeFilterValues = {
        my_initiatives: store.my_initiatives, include_deactivated: store.include_deactivated, categories: store.categories, companies: store.companies,
        dois: store.dois, complexity_tiers: store.complexity_tiers, yearsByDoi: store.yearsByDoi, excluded: store.excluded,
    };
    const [draft, change, dirty] = useSitFilterDraft(applied);
    const excl = (key: string) => draft.excluded.includes(key);
    const excluded = (key: string, excluding: boolean) => withExcluded(draft.excluded, key, excluding);
    const categories = ccStore.categoryRM.result ?? [];
    const companies = ccStore.companyRM.result ?? [];
    const views = [
        {value: 0, label: i18n.t('filters.allInitiatives', {ns: 'sit'})},
        {value: 1, label: i18n.t('filters.myInitiatives', {ns: 'sit'})},
    ];
    const deactivated = [
        {value: 0, label: i18n.t('filters.activeOnly', {ns: 'sit'})},
        {value: 1, label: i18n.t('filters.includingDeactivated', {ns: 'sit'})},
    ];
    const filters: SitFilterDef[] = [
        {
            id: 'view', label: i18n.t('filters.view', {ns: 'sit'}), primary: true,
            active: draft.my_initiatives !== 0, status: views.find(v => v.value === draft.my_initiatives)?.label ?? '',
            render: close => <SitOptionList options={views} selected={[draft.my_initiatives]}
                                            onChange={([v]) => { change({my_initiatives: v}); close(); }}/>,
        },
        {
            id: 'deactivated', label: i18n.t('filters.deactivated', {ns: 'sit'}),
            active: draft.include_deactivated, status: deactivated[Number(draft.include_deactivated)].label,
            render: close => <SitOptionList options={deactivated} selected={[Number(draft.include_deactivated)]}
                                            onChange={([v]) => { change({include_deactivated: v === 1}); close(); }}/>,
        },
        {
            id: 'category', label: sitColumnLabel('category'), active: draft.categories.length > 0,
            status: sitFilterStatus(draft.categories.map(c => category_readable_str(c)), excl('categories'), categories.length),
            render: () => <SitOptionList multiple options={categories.map(c => ({value: c.id, label: category_readable_str(c)}))}
                                         selected={draft.categories.map(c => c.id)} excluding={excl('categories')}
                                         onChange={(ids, x) => change({
                                             categories: categories.filter(c => ids.includes(c.id)), excluded: excluded('categories', x),
                                         })}/>,
        },
        {
            id: 'company', label: sitColumnLabel('company'), primary: true, active: draft.companies.length > 0,
            status: sitFilterStatus(draft.companies.map(c => c.title), excl('companies'), companies.length),
            render: () => <SitOptionList multiple options={companies.map(c => ({value: c.id, label: c.title}))}
                                         selected={draft.companies.map(c => c.id)} excluding={excl('companies')}
                                         onChange={(ids, x) => change({
                                             companies: companies.filter(c => ids.includes(c.id)), excluded: excluded('companies', x),
                                         })}/>,
        },
        {
            id: 'stage', label: sitColumnLabel('stage'), primary: true, active: draft.dois.length > 0,
            status: sitFilterStatus(draft.dois.map(stageGateLabel), excl('dois'), DOIs.length),
            render: () => <SitOptionList multiple options={DOIs.map(d => ({value: d, label: stageGateLabel(d)}))}
                                         selected={draft.dois} excluding={excl('dois')}
                                         onChange={(dois, x) => change({dois, excluded: excluded('dois', x)})}/>,
        },
        {
            id: 'complexity', label: sitFieldLabel('complexityTier'), active: draft.complexity_tiers.length > 0,
            status: sitFilterStatus(draft.complexity_tiers.map(complexityTierLabel), excl('complexity_tiers'), ComplexityTierValues.length),
            render: () => <SitOptionList multiple options={ComplexityTierValues.map(v => ({value: v, label: complexityTierLabel(v)}))}
                                         selected={draft.complexity_tiers} excluding={excl('complexity_tiers')}
                                         onChange={(complexity_tiers, x) => change({
                                             complexity_tiers, excluded: excluded('complexity_tiers', x),
                                         })}/>,
        },
        ...DOIs.map((doi: DOI): SitFilterDef => ({
            id: 'year-' + doi, label: `${sitFieldLabel('year')} (${stageGateLabel(doi)})`,
            active: draft.yearsByDoi[doi].length > 0,
            status: sitFilterStatus(draft.yearsByDoi[doi].map(String), excl(`year_${doi}`), yearOptions.length),
            render: () => <SitOptionList multiple options={yearOptions.map(y => ({value: y, label: String(y)}))}
                                         selected={draft.yearsByDoi[doi]} excluding={excl(`year_${doi}`)}
                                         onChange={(years, x) => change({
                                             yearsByDoi: {...draft.yearsByDoi, [doi]: years}, excluded: excluded(`year_${doi}`, x),
                                         })}/>,
        })),
    ];

    return (
        <SitFilterBar
            className="initiative-filters-component"
            filters={filters} dirty={dirty}
            clearable={dirty || JSON.stringify(applied) !== JSON.stringify(NO_FILTERS)}
            onApply={() => store.applyFilters(draft)}
            onClear={() => store.applyFilters(NO_FILTERS)}
            lead={<TextField
                className="sit-search-field"
                size="small"
                placeholder="Search initiatives"
                value={store.search}
                onChange={e => store.setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>,
                    endAdornment: store.search
                        ? <InputAdornment position="end">
                            <IconButton size="small" aria-label="clear search" onClick={() => store.setSearch('')}>
                                <Clear fontSize="small"/>
                            </IconButton>
                        </InputAdornment>
                        : undefined,
                }}/>}
            trail={<>
                {/*<Button variant="outlined" className="sit-export-btn" disabled={exporting}*/}
                {/*        startIcon={exporting ? <CircularProgress size={14}/> : <FileDownload/>}*/}
                {/*        onClick={onExport}>*/}
                {/*    Export*/}
                {/*</Button>*/}
                <Button variant="contained" className="sit-add-btn" startIcon={<Add/>}
                        onClick={() => navigate(generatePath(sitRoutes.sit_initiative, {initiativeId: 'new'}))}>
                    Add new initiative
                </Button>
            </>}/>
    );
});
